import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import { BackButton } from '../../components/BackButton';
import { ExportIconButtons } from '../../components/ExportIconButtons';
import {
  ActionMenu,
  Badge,
  BanIcon,
  Button,
  Callout,
  Card,
  CheckCircleIcon,
  ClockIcon,
  CoinIcon,
  DocumentTextIcon,
  ViewColumnsIcon,
  LinkChainIcon,
  ListEmptyState,
  LoadingSpinner,
  PencilIcon,
  SegmentedControl,
  TableIcon,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TrashIcon,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import {
  CustomerDetail,
  CustomerStatement,
  CustomerStatementEntry,
  MoneyTotals,
} from '../../types/customer';
import { InvoiceListItem } from '../../types/invoice';
import { PaymentListItem } from '../../types/payment';
import { formatDate, formatMoney } from '../../utils/format';
import {
  exportCustomerStatementExcel,
  exportCustomerStatementPdf,
} from './exportCustomerStatement';
import {
  exportCustomerInvoicesExcel,
  exportCustomerInvoicesPdf,
} from './exportCustomerInvoices';
import {
  exportCustomerPaymentsExcel,
  exportCustomerPaymentsPdf,
} from './exportCustomerPayments';
import { AutoAllocatePreviewModal } from '../payments/AutoAllocatePreviewModal';
import { CustomerMatchBoardModal } from './CustomerMatchBoardModal';
import { PaymentPaidInvoicesButton } from '../payments/PaymentPaidInvoicesModal';
import { useAutoAllocate } from '../payments/useAutoAllocate';

type TabKey = 'overview' | 'agreement' | 'invoices' | 'payments' | 'statement';
type InvoiceStatusFilter = 'all' | 'unpaid' | 'paid' | 'overdue';

const PILL_ICON_CLASS = 'h-3.5 w-3.5';

function invoiceStatusPills() {
  const filters = t().invoices.filters;
  return [
    {
      id: 'all' as const,
      label: filters.all,
      icon: <TableIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'unpaid' as const,
      label: filters.unpaid,
      icon: <DocumentTextIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'paid' as const,
      label: filters.paid,
      icon: <CheckCircleIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'overdue' as const,
      label: filters.overdue,
      icon: <ClockIcon className={PILL_ICON_CLASS} />,
    },
  ];
}

function filterStatementEntries(
  entries: CustomerStatementEntry[],
  view: InvoiceStatusFilter,
): CustomerStatementEntry[] {
  if (view === 'all') return entries;
  return entries.filter((entry) => {
    if (entry.type !== 'INVOICE') return false;
    if (view === 'unpaid') return entry.isOpen;
    if (view === 'paid') return !entry.isOpen;
    return entry.isOpen && (entry.daysDue ?? 0) > 0;
  });
}

const invoiceSortGetters: Record<
  string,
  (item: InvoiceListItem) => string | number | boolean | null | undefined
> = {
  invoiceDate: (i) => i.invoiceDate,
  invoiceNo: (i) => i.invoiceNo,
  dueDate: (i) => i.dueDate,
  daysDue: (i) => i.daysDue,
  category: (i) => i.category?.name,
  chargesTotal: (i) => i.charges.total,
  paid: (i) => i.paid.total,
  outstanding: (i) => i.outstanding.total,
  status: (i) => i.status,
};

const paymentSortGetters: Record<
  string,
  (item: PaymentListItem) => string | number | boolean | null | undefined
> = {
  paymentDate: (p) => p.paymentDate,
  invoiceNo: (p) => p.invoice?.invoiceNo,
  totalsInvoice: (p) => p.totals.invoice,
  totalsCash: (p) => p.totals.cash,
  totalsTotal: (p) => p.totals.total,
};

const statementSortGetters: Record<
  string,
  (entry: CustomerStatementEntry) => string | number | boolean | null | undefined
> = {
  date: (e) => e.date,
  type: (e) => e.type,
  reference: (e) => e.reference,
  dueDate: (e) => e.dueDate,
  daysDue: (e) => e.daysDue,
  amountsInvoice: (e) => e.amounts.invoice,
  amountsCash: (e) => e.amounts.cash,
  runningInvoice: (e) => e.runningBalance.invoice,
  runningCash: (e) => e.runningBalance.cash,
  runningTotal: (e) => e.runningBalance.total,
  outstandingInvoice: (e) => e.outstanding.invoice,
  outstandingCash: (e) => e.outstanding.cash,
  outstandingTotal: (e) => e.outstanding.total,
};

export function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const confirm = useConfirm();
  const labels = t().customers;
  const [tab, setTab] = useState<TabKey>('overview');
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [payments, setPayments] = useState<PaymentListItem[]>([]);
  const [statement, setStatement] = useState<CustomerStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [invoiceStatus, setInvoiceStatus] =
    useState<InvoiceStatusFilter>('all');
  const [statementView, setStatementView] =
    useState<InvoiceStatusFilter>('all');
  const [matchBoardOpen, setMatchBoardOpen] = useState(false);
  const [tabRefresh, setTabRefresh] = useState(0);
  const allocate = useAutoAllocate(() => ({
    customerId: id ? Number(id) : undefined,
  }));

  const invoiceSort = useTableSort('invoiceDate', 'desc');
  const paymentSort = useTableSort('paymentDate', 'desc');
  const statementSort = useTableSort('date', 'desc');

  const statementInvoiceOnly = statementView !== 'all';
  const displayInvoices = useMemo(
    () => sortItems(invoices, invoiceSort.sort, invoiceSortGetters),
    [invoices, invoiceSort.sort],
  );
  const displayPayments = useMemo(
    () => sortItems(payments, paymentSort.sort, paymentSortGetters),
    [payments, paymentSort.sort],
  );
  const displayEntries = useMemo(() => {
    if (!statement) return [];
    return sortItems(
      filterStatementEntries(statement.entries, statementView),
      statementSort.sort,
      statementSortGetters,
    );
  }, [statement, statementView, statementSort.sort]);

  const invoiceSums = useMemo(
    () => ({
      charges: sumMoney(invoices.map((item) => item.charges.total)),
      paid: sumMoney(invoices.map((item) => item.paid.total)),
      outstanding: sumMoney(invoices.map((item) => item.outstanding.total)),
    }),
    [invoices],
  );

  const paymentSums = useMemo(
    () => sumMoneyTotals(payments.map((item) => item.totals)),
    [payments],
  );

  const statementAmountSums = useMemo(() => {
    let invoice = 0;
    let cash = 0;
    for (const entry of displayEntries) {
      const sign = entry.direction === 'charge' ? 1 : -1;
      invoice += sign * Number(entry.amounts.invoice);
      cash += sign * Number(entry.amounts.cash);
    }
    return {
      invoice: invoice.toFixed(2),
      cash: cash.toFixed(2),
    };
  }, [displayEntries]);

  const statementRemainingSums = useMemo(
    () => sumMoneyTotals(displayEntries.map((entry) => entry.outstanding)),
    [displayEntries],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCustomer(Number(id));
        if (!cancelled) setCustomer(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !customer) return;
    let cancelled = false;

    async function loadTab() {
      if (tab === 'overview' || tab === 'agreement') return;
      setTabLoading(true);
      setError(null);
      try {
        if (tab === 'invoices') {
          const result = await api.getInvoices({
            customerId: Number(id),
            pageSize: 100,
            status:
              invoiceStatus && invoiceStatus !== 'all'
                ? invoiceStatus
                : undefined,
          });
          if (!cancelled) setInvoices(result.data);
        } else if (tab === 'payments') {
          const result = await api.getPayments({
            customerId: Number(id),
            pageSize: 100,
          });
          if (!cancelled) setPayments(result.data);
        } else if (tab === 'statement') {
          const result = await api.getCustomerStatement(Number(id));
          if (!cancelled) setStatement(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setTabLoading(false);
      }
    }

    void loadTab();
    return () => {
      cancelled = true;
    };
  }, [tab, id, customer, invoiceStatus, tabRefresh]);

  async function reloadCustomer() {
    if (!id) return;
    const data = await api.getCustomer(Number(id));
    setCustomer(data);
  }

  async function handleApplyAllocate() {
    const applied = await allocate.applySelected();
    if (!applied) return;
    try {
      await reloadCustomer();
      setTabRefresh((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function handleMatchApplied() {
    try {
      await reloadCustomer();
      setTabRefresh((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function handleDelete() {
    if (!customer) return;
    const ok = await confirm({
      title: labels.delete,
      message: labels.deleteConfirm.replace('{name}', customer.name),
      confirmText: labels.delete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteCustomer(customer.id);
      navigate('/customers');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
      setDeleting(false);
    }
  }

  async function toggleActive() {
    if (!customer) return;
    setToggling(true);
    setError(null);
    try {
      const updated = await api.updateCustomer(customer.id, {
        name: customer.name,
        active: !customer.active,
      });
      setCustomer(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    } finally {
      setToggling(false);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} />;
  }

  if (!customer) {
    return (
      <Callout variant="danger">{error || 'Ο πελάτης δεν βρέθηκε'}</Callout>
    );
  }

  const summary = customer.financialSummary ?? {
    charges: { invoice: '0.00', cash: '0.00', total: '0.00' },
    payments: { invoice: '0.00', cash: '0.00', total: '0.00' },
    outstanding: customer.outstandingBalance,
  };

  const loadedCustomer = customer;
  const canExportInvoices = invoices.length > 0 && !tabLoading;
  const canExportPayments = payments.length > 0 && !tabLoading;
  const canExportStatement = displayEntries.length > 0 && !tabLoading;

  function handleExportInvoicesExcel() {
    exportCustomerInvoicesExcel(loadedCustomer, displayInvoices);
  }

  function handleExportInvoicesPdf() {
    exportCustomerInvoicesPdf(loadedCustomer, displayInvoices);
  }

  function handleExportPaymentsExcel() {
    exportCustomerPaymentsExcel(loadedCustomer, displayPayments);
  }

  function handleExportPaymentsPdf() {
    exportCustomerPaymentsPdf(loadedCustomer, displayPayments);
  }

  function handleExportStatementExcel() {
    if (!statement) return;
    exportCustomerStatementExcel(
      loadedCustomer,
      statement,
      displayEntries,
      statementInvoiceOnly,
    );
  }

  function handleExportStatementPdf() {
    if (!statement) return;
    exportCustomerStatementPdf(
      loadedCustomer,
      statement,
      displayEntries,
      statementInvoiceOnly,
    );
  }

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'overview', label: labels.tabs.overview },
    ...(user?.features?.includes('agreements')
      ? [{ key: 'agreement' as const, label: labels.tabs.agreement }]
      : []),
    { key: 'invoices', label: labels.tabs.invoices },
    { key: 'payments', label: labels.tabs.payments },
    { key: 'statement', label: labels.tabs.statement },
  ];

  return (
    <PageShell>
      <Card className="shrink-0 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">{customer.name}</h2>
            <div className="mt-1 text-sm text-ink-muted">
              {customer.vatNumber
                ? `${t().common.vatPrefix}: ${customer.vatNumber}`
                : t().common.noVat}
              {customer.phone ? ` · ${customer.phone}` : ''}
              {customer.email ? ` · ${customer.email}` : ''}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <div>
                <div className="text-xs text-ink-faint">{labels.agreedAmount}</div>
                <div className="font-semibold">
                  {formatMoney(customer.agreedAmount.total)}
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-faint">{labels.balance}</div>
                <div className="font-semibold">
                  {formatMoney(summary.outstanding.total)}
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-faint">{labels.status}</div>
                <Badge
                  variant={customer.active ? 'success' : 'secondary'}
                  className="mt-0.5"
                >
                  {customer.active ? labels.active : labels.inactive}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <BackButton fallback="/customers" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/customers/${customer.id}/edit`)}
            >
              <PencilIcon className="h-3.5 w-3.5" />
              {labels.editAction}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/invoices/new')}
            >
              <DocumentTextIcon className="h-3.5 w-3.5" />
              {t().invoices.new}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/payments/new')}
            >
              <CoinIcon className="h-3.5 w-3.5" />
              {t().payments.new}
            </Button>
            <ActionMenu
              label={t().common.actions}
              loading={allocate.previewing || toggling || deleting}
              items={[
                {
                  id: 'auto-allocate',
                  label: t().reports.autoAllocate,
                  icon: <LinkChainIcon />,
                  disabled: allocate.previewing,
                  onSelect: () => void allocate.openPreview(),
                },
                {
                  id: 'match-board',
                  label: labels.matchBoard,
                  icon: <ViewColumnsIcon />,
                  onSelect: () => setMatchBoardOpen(true),
                },
                {
                  id: 'toggle-active',
                  label: customer.active ? labels.deactivate : labels.activate,
                  icon: customer.active ? <BanIcon /> : <CheckCircleIcon />,
                  disabled: toggling,
                  separatorBefore: true,
                  onSelect: () => void toggleActive(),
                },
                {
                  id: 'delete',
                  label: labels.delete,
                  icon: <TrashIcon />,
                  danger: true,
                  hidden: !isAdmin,
                  disabled: deleting,
                  onSelect: () => void handleDelete(),
                },
              ]}
            />
          </div>
        </div>
      </Card>

      {allocate.success ? (
        <Callout variant="success" className="shrink-0">
          {allocate.success}
        </Callout>
      ) : null}

      {error || allocate.error ? (
        <Callout variant="danger" className="shrink-0">
          {error || allocate.error}
        </Callout>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as TabKey)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="shrink-0">
          {tabs.map((item) => (
            <TabsTrigger key={item.key} value={item.key}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="min-h-0 flex-1 overflow-auto">
          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <TotalsCard title={labels.totalCharges} totals={summary.charges} />
            <TotalsCard title={labels.totalPayments} totals={summary.payments} />
            <TotalsCard title={labels.balance} totals={summary.outstanding} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <InfoCard title="Στοιχεία πελάτη">
              <InfoRow label={labels.name} value={customer.name} />
              <InfoRow label={labels.vatNumber} value={customer.vatNumber} />
              <InfoRow label={labels.email} value={customer.email} />
              <InfoRow label={labels.phone} value={customer.phone} />
              <InfoRow label={labels.address} value={customer.address} />
              <InfoRow
                label={labels.accountingBooksCategory}
                value={
                  customer.accountingBooksCategory === 'B_SINGLE_ENTRY'
                    ? labels.booksSingleEntry
                    : customer.accountingBooksCategory === 'C_DOUBLE_ENTRY'
                      ? labels.booksDoubleEntry
                      : null
                }
              />
              <InfoRow label={labels.prefecture} value={customer.prefecture} />
              <InfoRow
                label={labels.creditDays}
                value={String(customer.creditDays ?? 0)}
              />
              <InfoRow
                label={labels.assignedUser}
                value={customer.assignedUser?.name}
              />
              <InfoRow
                label={labels.responsibleOffice}
                value={customer.office?.name}
              />
              <InfoRow label={labels.notes} value={customer.notes} />
            </InfoCard>
            <InfoCard title="Συμφωνία">
              <InfoRow
                label={labels.agreedAmount}
                value={formatMoney(customer.agreedAmount.total)}
              />
              <InfoRow
                label={`${labels.agreedAmount} — ${t().financial.invoice}`}
                value={formatMoney(customer.agreedAmount.invoice)}
              />
              <InfoRow
                label={`${labels.agreedAmount} — ${t().financial.cash}`}
                value={formatMoney(customer.agreedAmount.cash)}
              />
              <InfoRow
                label={labels.balance}
                value={formatMoney(summary.outstanding.total)}
              />
            </InfoCard>
          </div>
        </TabsContent>

        <TabsContent value="agreement" className="min-h-0 flex-1 overflow-auto">
          <Card className="p-5">
            {!customer.agreement || customer.agreement.lines.length === 0 ? (
              <ListEmptyState title={labels.noAgreement} />
            ) : (
              <div className="space-y-4">
                {customer.agreement.notes ? (
                  <p className="text-sm text-ink-muted">
                    {customer.agreement.notes}
                  </p>
                ) : null}
                <table className="min-w-full text-sm">
                  <thead className="bg-surface-muted text-left text-ink-muted">
                    <tr>
                      <th className="px-3 py-2">{t().financial.type}</th>
                      <th className="px-3 py-2 text-right">
                        {t().financial.amount}
                      </th>
                      <th className="px-3 py-2">{t().financial.description}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.agreement.lines.map((line) => (
                      <tr key={line.id} className="border-t border-line">
                        <td className="px-3 py-2">
                          {line.type === 'INVOICE'
                            ? t().financial.invoice
                            : t().financial.cash}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatMoney(line.amount)}
                        </td>
                        <td className="px-3 py-2">{line.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="space-y-1 text-right text-sm">
                  <div>
                    {t().financial.invoiceTotal}:{' '}
                    {formatMoney(customer.agreement.totals.invoice)}
                  </div>
                  <div>
                    {t().financial.cashTotal}:{' '}
                    {formatMoney(customer.agreement.totals.cash)}
                  </div>
                  <div className="font-semibold">
                    {t().financial.grandTotal}:{' '}
                    {formatMoney(customer.agreement.totals.total)}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent
          value="invoices"
          className="mt-4 flex min-h-0 flex-1 flex-col"
        >
          <ExportToolbar
            disabled={!canExportInvoices}
            onExcel={handleExportInvoicesExcel}
            onPdf={handleExportInvoicesPdf}
            extra={
              <SegmentedControl
                value={invoiceStatus}
                onChange={setInvoiceStatus}
                size="sm"
                aria-label={labels.invoiceStatusView}
                options={invoiceStatusPills()}
              />
            }
          />
          <DataTablePanel>
            {tabLoading ? (
              <LoadingSpinner message={t().common.loading} />
            ) : invoices.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="invoiceDate"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                    >
                      {t().invoices.invoiceDate}
                    </SortableTh>
                    <SortableTh
                      sortKey="invoiceNo"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                    >
                      {t().invoices.invoiceNo}
                    </SortableTh>
                    <SortableTh
                      sortKey="dueDate"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                    >
                      {t().invoices.dueDate}
                    </SortableTh>
                    <SortableTh
                      sortKey="daysDue"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                      align="right"
                    >
                      {t().invoices.daysDue}
                    </SortableTh>
                    <SortableTh
                      sortKey="category"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                    >
                      {t().invoices.category}
                    </SortableTh>
                    <SortableTh
                      sortKey="chargesTotal"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                      align="right"
                    >
                      {t().financial.grandTotal}
                    </SortableTh>
                    <SortableTh
                      sortKey="paid"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                      align="right"
                    >
                      {t().invoices.paid}
                    </SortableTh>
                    <SortableTh
                      sortKey="outstanding"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                      align="right"
                    >
                      {t().invoices.outstanding}
                    </SortableTh>
                    <SortableTh
                      sortKey="status"
                      sort={invoiceSort.sort}
                      onSort={invoiceSort.toggleSort}
                    >
                      {t().invoices.status}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-line">
                      <td className="px-3 py-2">
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="text-brand hover:underline"
                        >
                          {invoice.invoiceNo}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <DaysDueValue
                          daysDue={invoice.daysDue}
                          emphasize={
                            invoice.status === 'OVERDUE' ||
                            (invoice.status !== 'PAID' &&
                              invoice.daysDue != null)
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        {invoice.category?.name || '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(invoice.charges.total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(invoice.paid.total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(invoice.outstanding.total)}
                      </td>
                      <td className="px-3 py-2">
                        {t().invoices.statuses[invoice.status]}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={dataTableFootClass}>
                  <tr>
                    <td className="px-3 py-2" colSpan={5}>
                      {t().common.total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(invoiceSums.charges)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(invoiceSums.paid)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(invoiceSums.outstanding)}
                    </td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            )}
          </DataTablePanel>
        </TabsContent>

        <TabsContent
          value="payments"
          className="mt-4 flex min-h-0 flex-1 flex-col"
        >
          <ExportToolbar
            disabled={!canExportPayments}
            onExcel={handleExportPaymentsExcel}
            onPdf={handleExportPaymentsPdf}
            extra={
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMatchBoardOpen(true)}
                >
                  <ViewColumnsIcon className="h-3.5 w-3.5" />
                  {labels.matchBoard}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={allocate.previewing}
                  disabled={allocate.previewing}
                  onClick={() => void allocate.openPreview()}
                >
                  <LinkChainIcon className="h-3.5 w-3.5" />
                  {t().reports.autoAllocate}
                </Button>
              </div>
            }
          />
          <DataTablePanel>
            {tabLoading ? (
              <LoadingSpinner message={t().common.loading} />
            ) : payments.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="paymentDate"
                      sort={paymentSort.sort}
                      onSort={paymentSort.toggleSort}
                    >
                      {t().payments.paymentDate}
                    </SortableTh>
                    <SortableTh
                      sortKey="invoiceNo"
                      sort={paymentSort.sort}
                      onSort={paymentSort.toggleSort}
                    >
                      {t().payments.invoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="totalsInvoice"
                      sort={paymentSort.sort}
                      onSort={paymentSort.toggleSort}
                      align="right"
                    >
                      {t().financial.invoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="totalsCash"
                      sort={paymentSort.sort}
                      onSort={paymentSort.toggleSort}
                      align="right"
                    >
                      {t().financial.cash}
                    </SortableTh>
                    <SortableTh
                      sortKey="totalsTotal"
                      sort={paymentSort.sort}
                      onSort={paymentSort.toggleSort}
                      align="right"
                    >
                      {t().payments.paymentTotal}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayPayments.map((payment) => (
                    <tr key={payment.id} className="border-t border-line">
                      <td className="px-3 py-2">
                        <Link
                          to={`/payments/${payment.id}`}
                          className="text-brand hover:underline"
                        >
                          {formatDate(payment.paymentDate)}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        {payment.isMultiAllocated ? (
                          <span>
                            {t().payments.multiAllocated}
                            <span className="mt-0.5 block text-xs text-ink-muted">
                              {payment.allocations
                                .map((a) => a.invoiceNo)
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          </span>
                        ) : payment.invoice ? (
                          <Link
                            to={`/invoices/${payment.invoice.id}`}
                            className="text-brand hover:underline"
                          >
                            {payment.invoice.invoiceNo}
                          </Link>
                        ) : payment.allocations[0] ? (
                          <Link
                            to={`/invoices/${payment.allocations[0].invoiceId}`}
                            className="text-brand hover:underline"
                          >
                            {payment.allocations[0].invoiceNo}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">
                            {t().payments.unallocated}
                          </span>
                        )}
                        <span className="ml-1 inline-flex align-middle">
                          <PaymentPaidInvoicesButton
                            allocations={payment.allocations}
                          />
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(payment.totals.invoice)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(payment.totals.cash)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(payment.totals.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={dataTableFootClass}>
                  <tr>
                    <td className="px-3 py-2" colSpan={2}>
                      {t().common.total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(paymentSums.invoice)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(paymentSums.cash)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(paymentSums.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </DataTablePanel>
        </TabsContent>

        <TabsContent
          value="statement"
          className="mt-4 flex min-h-0 flex-1 flex-col"
        >
          <ExportToolbar
            disabled={!canExportStatement}
            onExcel={handleExportStatementExcel}
            onPdf={handleExportStatementPdf}
            extra={
              <SegmentedControl
                value={statementView}
                onChange={setStatementView}
                size="sm"
                aria-label={labels.statementView}
                options={invoiceStatusPills()}
              />
            }
          />
          <DataTablePanel>
            {tabLoading ? (
              <LoadingSpinner message={t().common.loading} />
            ) : !statement || displayEntries.length === 0 ? (
              <ListEmptyState
                title={
                  statementView === 'unpaid'
                    ? labels.statementOpenEmpty
                    : statementView === 'paid'
                      ? labels.statementPaidEmpty
                      : statementView === 'overdue'
                        ? labels.statementOverdueEmpty
                        : labels.statementEmpty
                }
              />
            ) : (
              <table className="min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="date"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                    >
                      {t().common.date}
                    </SortableTh>
                    <SortableTh
                      sortKey="type"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                    >
                      {labels.movementType}
                    </SortableTh>
                    <SortableTh
                      sortKey="reference"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                    >
                      {labels.reference}
                    </SortableTh>
                    <SortableTh
                      sortKey="dueDate"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                    >
                      {t().invoices.dueDate}
                    </SortableTh>
                    <SortableTh
                      sortKey="daysDue"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {t().invoices.daysDue}
                    </SortableTh>
                    <SortableTh
                      sortKey="amountsInvoice"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {t().financial.invoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="amountsCash"
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {t().financial.cash}
                    </SortableTh>
                    <SortableTh
                      sortKey={
                        statementInvoiceOnly ? 'outstandingInvoice' : 'runningInvoice'
                      }
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {labels.runningInvoice}
                    </SortableTh>
                    <SortableTh
                      sortKey={
                        statementInvoiceOnly ? 'outstandingCash' : 'runningCash'
                      }
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {labels.runningCash}
                    </SortableTh>
                    <SortableTh
                      sortKey={
                        statementInvoiceOnly ? 'outstandingTotal' : 'runningTotal'
                      }
                      sort={statementSort.sort}
                      onSort={statementSort.toggleSort}
                      align="right"
                    >
                      {labels.runningTotal}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayEntries.map((entry) => (
                    <tr
                      key={`${entry.type}-${entry.id}`}
                      className="border-t border-line"
                    >
                      <td className="px-3 py-2">{formatDate(entry.date)}</td>
                      <td className="px-3 py-2">
                        {entry.type === 'INVOICE'
                          ? labels.invoiceMovement
                          : labels.paymentMovement}
                      </td>
                      <td className="px-3 py-2">
                        {entry.type === 'INVOICE' ? (
                          <Link
                            to={`/invoices/${entry.id}`}
                            className="text-brand hover:underline"
                          >
                            {entry.reference}
                          </Link>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="min-w-0">
                              <Link
                                to={`/payments/${entry.id}`}
                                className="text-brand hover:underline"
                              >
                                {entry.reference}
                              </Link>
                              {entry.description ? (
                                <div
                                  className="truncate text-xs text-ink-muted"
                                  title={entry.description}
                                >
                                  {entry.description}
                                </div>
                              ) : null}
                            </div>
                            <PaymentPaidInvoicesButton
                              allocations={entry.allocations ?? []}
                            />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {entry.type === 'INVOICE'
                          ? formatDate(entry.dueDate)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {entry.type === 'INVOICE' ? (
                          <DaysDueValue
                            daysDue={entry.daysDue}
                            emphasize={Boolean(entry.isOpen && entry.daysDue)}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {entry.direction === 'charge' ? '' : '−'}
                        {formatMoney(entry.amounts.invoice)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {entry.direction === 'charge' ? '' : '−'}
                        {formatMoney(entry.amounts.cash)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(
                          statementInvoiceOnly
                            ? entry.outstanding.invoice
                            : entry.runningBalance.invoice,
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(
                          statementInvoiceOnly
                            ? entry.outstanding.cash
                            : entry.runningBalance.cash,
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(
                          statementInvoiceOnly
                            ? entry.outstanding.total
                            : entry.runningBalance.total,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={dataTableFootClass}>
                  <tr>
                    <td className="px-3 py-2" colSpan={5}>
                      {t().common.total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(statementAmountSums.invoice)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(statementAmountSums.cash)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(
                        statementInvoiceOnly
                          ? statementRemainingSums.invoice
                          : statement.summary.outstanding.invoice,
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(
                        statementInvoiceOnly
                          ? statementRemainingSums.cash
                          : statement.summary.outstanding.cash,
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(
                        statementInvoiceOnly
                          ? statementRemainingSums.total
                          : statement.summary.outstanding.total,
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </DataTablePanel>
        </TabsContent>
      </Tabs>

      <AutoAllocatePreviewModal
        isOpen={allocate.previewOpen}
        preview={allocate.preview}
        applying={allocate.applying}
        selectedKeys={allocate.selectedKeys}
        previewKeys={allocate.previewKeys}
        allSelected={allocate.allSelected}
        someSelected={allocate.someSelected}
        hideCustomer
        allowRematch={allocate.allowRematch}
        replaceExisting={allocate.replaceExisting}
        previewing={allocate.previewing}
        onReplaceExistingChange={(next) =>
          void allocate.changeReplaceExisting(next)
        }
        onClose={allocate.closePreview}
        onToggle={allocate.toggleSelection}
        onSelectAll={(keys) => allocate.setSelectedKeys(new Set(keys))}
        onApply={() => void handleApplyAllocate()}
      />
      {id ? (
        <CustomerMatchBoardModal
          isOpen={matchBoardOpen}
          customerId={Number(id)}
          onClose={() => setMatchBoardOpen(false)}
          onApplied={handleMatchApplied}
        />
      ) : null}
    </PageShell>
  );
}

function DaysDueValue({
  daysDue,
  emphasize,
}: {
  daysDue: number | null | undefined;
  emphasize?: boolean;
}) {
  if (daysDue == null || daysDue <= 0) {
    return <span className="text-ink-muted">—</span>;
  }

  return (
    <span
      className={
        emphasize
          ? 'font-medium text-red-700 dark:text-red-400'
          : undefined
      }
    >
      {daysDue}
    </span>
  );
}

function sumMoney(values: string[]): string {
  return values.reduce((acc, value) => acc + Number(value), 0).toFixed(2);
}

function sumMoneyTotals(items: MoneyTotals[]): MoneyTotals {
  return {
    invoice: sumMoney(items.map((item) => item.invoice)),
    cash: sumMoney(items.map((item) => item.cash)),
    total: sumMoney(items.map((item) => item.total)),
  };
}

function ExportToolbar({
  disabled,
  onExcel,
  onPdf,
  extra,
}: {
  disabled: boolean;
  onExcel: () => void;
  onPdf: () => void;
  extra?: ReactNode;
}) {
  const labels = t().customers;
  return (
    <div
      className={`mb-2 flex shrink-0 flex-wrap items-center gap-2 ${
        extra ? 'justify-between' : 'justify-end'
      }`}
    >
      {extra}
      <ExportIconButtons
        disabled={disabled}
        onExcel={onExcel}
        onPdf={onPdf}
        excelLabel={labels.exportExcel}
        pdfLabel={labels.exportPdf}
      />
    </div>
  );
}

function TotalsCard({ title, totals }: { title: string; totals: MoneyTotals }) {
  return (
    <Card className="p-4 text-sm">
      <div className="text-xs uppercase tracking-wide text-ink-faint">{title}</div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between">
          <span>{t().financial.invoice}</span>
          <span className="tabular-nums">{formatMoney(totals.invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t().financial.cash}</span>
          <span className="tabular-nums">{formatMoney(totals.cash)}</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(totals.total)}</span>
        </div>
      </div>
    </Card>
  );
}

function InfoCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="space-y-2">{children}</div>
    </Card>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right text-ink">{value || '—'}</span>
    </div>
  );
}
